import { useEffect, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { schema } from './Schema';
import { z } from 'zod';
import CreatableSelect from 'react-select/creatable';
import clsx from 'clsx';
import React from 'react';

type BrandOption = {
  value: string;
  label: string;
  sector: string;
  __isNew__: boolean;
};

interface Props {
  defaultValues: z.infer<ReturnType<typeof schema>['addBrands']>['brands'][number];
  index: number;
  isLoading: boolean;
  onRemove: (index: number) => void;
  brandListOptions: any[];
  virtualMenu?: React.ReactNode;
}

const SiAiAddBrandsRow = React.memo(
  ({ defaultValues, index, isLoading, onRemove, brandListOptions, virtualMenu }: Props) => {
    const intl = useIntl();
    const DEFAULT_SECTOR = 'OTHER';
    const DEFAULT_SEARCH_TYPE = 'EXACT_MATCH';

    const { addBrands } = schema(intl);
    type FormDataType = z.infer<typeof addBrands>;

    const { control, watch, setValue } = useFormContext<FormDataType>();
    const selectedBrandName = watch(`brands.${index}.name`);
    const isNewBrand = watch(`brands.${index}.isNew`) ?? false;

    useEffect(() => {
      setValue(`brands.${index}.sector`, DEFAULT_SECTOR);
      setValue(`brands.${index}.searchType`, DEFAULT_SEARCH_TYPE);
      setValue(`brands.${index}.screenshot`, null);
    }, [DEFAULT_SEARCH_TYPE, DEFAULT_SECTOR, index, setValue]);

    // Build selectable options from all known brands so users can search by name first.
    const brandOptions = useMemo<BrandOption[]>(() => {
      if (!(brandListOptions?.length > 0)) return [];
      return brandListOptions.map((brand) => ({
        value: brand?.name,
        label: brand?.name,
        sector: brand?.sector,
        __isNew__: false,
      }));
    }, [brandListOptions]);

    const selectedBrandOption = useMemo<BrandOption | null>(() => {
      if (!selectedBrandName) return null;

      const matchedExistingBrand = brandOptions.find(
        (option) => option.value === selectedBrandName
      );

      if (matchedExistingBrand) {
        return { ...matchedExistingBrand, __isNew__: isNewBrand };
      }

      return {
        label: selectedBrandName,
        value: selectedBrandName,
        sector: DEFAULT_SECTOR,
        __isNew__: isNewBrand,
      };
    }, [DEFAULT_SECTOR, brandOptions, isNewBrand, selectedBrandName]);

    return (
      <div className='d-flex justify-content-between align-items-start gap-4 mb-1'>
        <div className='w-100'>
          {index === 0 && <label className='form-label mb-2 required'>Brand Name</label>}
          <Controller
            name={`brands.${index}.name`}
            control={control}
            render={({ field, fieldState }) => {
              return (
                <CreatableSelect<BrandOption, false>
                  placeholder={intl.formatMessage({ id: 'COMMON.SELECT' })}
                  noOptionsMessage={() => intl.formatMessage({ id: 'COMMON.SELECT' })}
                  className={`w-100 ${fieldState.invalid ? 'border rounded border-danger' : ''}`}
                  options={brandOptions}
                  value={selectedBrandOption}
                  getNewOptionData={(inputValue) => ({
                    value: inputValue,
                    label: inputValue,
                    sector: '',
                    __isNew__: true,
                  })}
                  isValidNewOption={(inputValue) => {
                    const trimmedInput = inputValue?.trim();
                    if (!trimmedInput) return false;
                    return !brandOptions.some(
                      (option) => option.value.toLowerCase() === trimmedInput.toLowerCase()
                    );
                  }}
                  onChange={(e) => {
                    if (!e) {
                      setValue(`brands.${index}.isNew`, defaultValues.isNew);
                      setValue(`brands.${index}.sector`, DEFAULT_SECTOR);
                      setValue(`brands.${index}.searchType`, DEFAULT_SEARCH_TYPE);
                      setValue(`brands.${index}.screenshot`, null);
                      field.onChange(defaultValues.name);
                      return;
                    }

                    const selectedOption = e as BrandOption;
                    const isNew = Boolean(selectedOption?.__isNew__);

                    setValue(`brands.${index}.isNew`, isNew);
                    field.onChange(selectedOption?.value ?? '');
                    setValue(`brands.${index}.sector`, DEFAULT_SECTOR);
                    setValue(`brands.${index}.searchType`, DEFAULT_SEARCH_TYPE);
                    setValue(`brands.${index}.screenshot`, null);
                  }}
                  isDisabled={isLoading}
                  // To prevent cutting-off dropdown when parent has overflow set
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              );
            }}
          />
        </div>

        <div className='d-flex align-items-center'>
          <i
            className={clsx(
              'bi bi-dash-square-fill fs-1',
              index > 0 ? 'text-danger cursor-pointer' : 'mt-8'
            )}
            onClick={() => {
              if (index > 0) {
                onRemove(index);
              }
            }}
          ></i>
        </div>
      </div>
    );
  }
);
export { SiAiAddBrandsRow };
